import { useEffect, useRef, useState } from "react";
import { X, Check, ZoomIn, Move } from "lucide-react";

const OUT_W = 1280, OUT_H = 720;

// Drag-to-position + zoom crop onto a fixed 16:9 frame. transform-origin is top-left so the
// preview maps 1:1 to the output canvas (point px -> pos + zoom*px), scaled by OUT_W/boxWidth.
export default function ThumbCropModal({ image, onCancel, onConfirm }) {
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [boxW, setBoxW] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const boxH = boxW * OUT_H / OUT_W;

  const clamp = (p, z, natR) => {
    const dispW = boxW * z;
    const dispH = boxW * natR * z;
    const x = Math.min(0, Math.max(boxW - dispW, p.x));
    const y = Math.min(0, Math.max(boxH - dispH, p.y));
    return { x, y };
  };

  const init = (w, h, bw) => {
    if (!w || !h || !bw) return;
    const natR = h / w;                       // height per unit width
    const cover = Math.max(1, (OUT_H / OUT_W) / natR); // zoom so 16:9 box is fully covered
    setMinZoom(cover);
    setZoom(cover);
    const dispW = bw * cover, dispH = bw * natR * cover;
    setPos({ x: (bw - dispW) / 2, y: (bw * OUT_H / OUT_W - dispH) / 2 });
  };

  useEffect(() => {
    const bw = boxRef.current?.clientWidth || 0;
    setBoxW(bw);
    if (nat.w && bw) init(nat.w, nat.h, bw);
  }, [nat, image]); // eslint-disable-line

  const onImgLoad = (e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight });

  const onZoom = (z) => {
    const natR = nat.h / nat.w;
    // keep center point stable while zooming
    const cx = boxW / 2, cy = boxH / 2;
    const ux = (cx - pos.x) / zoom, uy = (cy - pos.y) / zoom;
    const nx = cx - ux * z, ny = cy - uy * z;
    setZoom(z);
    setPos(clamp({ x: nx, y: ny }, z, natR));
  };

  const start = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, px: pos.x, py: pos.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const move = (e) => {
    if (!drag.current) return;
    const natR = nat.h / nat.w;
    setPos(clamp({ x: drag.current.px + (e.clientX - drag.current.sx), y: drag.current.py + (e.clientY - drag.current.sy) }, zoom, natR));
  };
  const end = () => { drag.current = null; };

  const confirm = () => {
    const ratio = OUT_W / boxW;
    const c = document.createElement("canvas");
    c.width = OUT_W; c.height = OUT_H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, OUT_W, OUT_H);
    const dW = OUT_W * zoom;
    const dH = OUT_W * (nat.h / nat.w) * zoom;
    ctx.drawImage(imgRef.current, pos.x * ratio, pos.y * ratio, dW, dH);
    onConfirm(c.toDataURL("image/jpeg", 0.9));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onCancel} data-testid="thumb-crop-overlay">
      <div onClick={(e) => e.stopPropagation()} className="sms-tweed w-full max-w-xl rounded-3xl border-2 border-[#5d8a52] bg-[#0f0f16] text-white p-5 sms-fade-up" style={{ boxShadow: "0 30px 90px -24px rgba(0,0,0,.85)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg flex items-center gap-2"><Move size={18} className="text-[#8fbf82]" /> Position &amp; zoom your thumbnail</div>
          <button onClick={onCancel} data-testid="thumb-crop-close" className="text-white/50 hover:text-white transition-colors"><X size={18} /></button>
        </div>
        <div
          ref={boxRef}
          data-testid="thumb-crop-frame"
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
          className="relative w-full overflow-hidden rounded-xl bg-black cursor-move select-none touch-none"
          style={{ aspectRatio: "16 / 9" }}
        >
          <img
            ref={imgRef}
            src={image}
            alt="crop"
            onLoad={onImgLoad}
            draggable={false}
            style={{ position: "absolute", left: 0, top: 0, width: "100%", transformOrigin: "0 0", transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`, pointerEvents: "none" }}
          />
          <div className="pointer-events-none absolute inset-0 ring-1 ring-white/20 rounded-xl" />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <ZoomIn size={16} className="text-[#8fbf82] shrink-0" />
          <input
            data-testid="thumb-crop-zoom"
            type="range" min={minZoom} max={minZoom * 3} step={0.01} value={zoom}
            onChange={(e) => onZoom(parseFloat(e.target.value))}
            className="w-full accent-[#4f7746]"
          />
        </div>
        <p className="text-[11px] text-white/45 mt-1">Drag the image to reposition · slide to zoom. Output is a crisp 1280×720 cover.</p>
        <button data-testid="thumb-crop-confirm" onClick={confirm} className="mt-4 w-full flex items-center justify-center gap-2 font-extrabold py-3 rounded-xl text-white bg-[#dc2626] hover:bg-[#b91c1c] transition-colors">
          <Check size={18} /> Use This Thumbnail
        </button>
      </div>
    </div>
  );
}
